import { LinearProgress } from '@mui/joy';

import { ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ErrorElement } from '@/ErrorElement';
import { useDatadogContext } from '@/context/hooks/useDatadogContext';
import { useAuth, useUserSSOInfo } from '@/hooks/useAuth/useAuth';
import { InstanceFlags, useInfo } from '@/hooks/useInstanceFlag';
import { useStoredOrganizationId } from '@/hooks/useStoredOrganizationId';
import { useOrganizationId, useTenantList } from '@/hooks/useSwr';
import {
  LATEST_TENANT_STORAGE_KEY,
  appOrganizationPath,
} from '@/utils/constants';
import { isSelfHostedSSO } from '@/utils/is-self-hosted';

import { useFallbackTenant } from './useFallbackTenant';

export function StorageLastTenant() {
  const { authed } = useAuth();
  const tenantId = useOrganizationId();
  const tenantList = useTenantList({ skip: !authed, skipCreate: true });
  const { setOrgId } = useStoredOrganizationId();

  useEffect(() => {
    if (tenantId && authed) {
      window.localStorage.setItem(LATEST_TENANT_STORAGE_KEY, tenantId);

      // Find the org related to the tenant that was updated
      if (tenantList?.isLoading) return;

      const targetTenant = tenantList.data?.find(
        (tenant) => tenant.id === tenantId
      );

      if (targetTenant) {
        window.localStorage.setItem(
          `${LATEST_TENANT_STORAGE_KEY}-${targetTenant.organization_id}`,
          targetTenant.id
        );
      }

      setOrgId((oldOrgId) => {
        if (
          !targetTenant?.organization_id ||
          oldOrgId === targetTenant.organization_id
        )
          return oldOrgId;

        return targetTenant.organization_id;
      });
    }
  }, [tenantId, tenantList, setOrgId, authed]);

  return null;
}

export function RedirectNoMatch(props: { children?: ReactNode }) {
  const { datadogLogs } = useDatadogContext();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    data: info,
    isLoading: isPersonalOrgsLoading,
    error: infoError,
  } = useInfo();
  const personal_orgs_disabled =
    info?.instance_flags?.[InstanceFlags.personal_orgs_disabled];

  const jitPollStartRef = useRef<number | null>(null);
  const [jitPollingExpired, setJitPollingExpired] = useState(false);
  const JIT_REFRESH_INTERVAL_MS = 500;
  const JIT_MAX_POLL_MS = 10_000; // 10 seconds of polling

  const selfHostedJitProvisioningEnabled =
    info?.instance_flags?.[InstanceFlags.self_hosted_jit_provisioning_enabled];

  const {
    tenant: targetTenant,
    isLoading,
    error: fallbackTenantError,
  } = useFallbackTenant({
    skipCreate: true,
    // Cap the polling for self-hosted SSO with JIT provisioning
    refreshInterval: (data) => {
      // Never refresh if not self-hosted SSO or JIT provisioning is disabled
      if (!(isSelfHostedSSO && selfHostedJitProvisioningEnabled)) {
        if (jitPollingExpired) setJitPollingExpired(false);
        jitPollStartRef.current = null;
        return 0;
      }
      // Never refresh if we have a tenant
      if (data && data.length > 0) {
        if (jitPollingExpired) setJitPollingExpired(false);
        return 0;
      }
      // Start polling if we don't have a tenant
      if (jitPollStartRef.current == null) {
        jitPollStartRef.current = Date.now();
      }
      const elapsed = Date.now() - (jitPollStartRef.current ?? Date.now());
      // Stop polling if we've exceeded the max polling time
      if (elapsed >= JIT_MAX_POLL_MS) {
        if (!jitPollingExpired) setJitPollingExpired(true);
        return 0;
      }
      return JIT_REFRESH_INTERVAL_MS;
    },
  });

  const { orgId: storedOrgId } = useStoredOrganizationId();
  const { isSSOLogin } = useUserSSOInfo();

  const requiredInfoLoading = isPersonalOrgsLoading || isLoading;

  const [redirect, setRedirect] = useState<null | boolean>(null);

  useEffect(() => {
    if (
      targetTenant != null &&
      !location.pathname.startsWith(`/${appOrganizationPath}/`)
    ) {
      const redirectPath = `/${appOrganizationPath}/${targetTenant.id}${location.pathname}`;
      setRedirect(true);
      navigate(redirectPath, { replace: true });
    } else if (
      targetTenant == null &&
      !requiredInfoLoading &&
      !personal_orgs_disabled &&
      !fallbackTenantError &&
      !infoError &&
      !isSSOLogin && // Don't redirect SAML users to onboarding
      !isSelfHostedSSO // Don't redirect self-hosted users to onboarding
    ) {
      setRedirect(true);
      datadogLogs?.logger.warn(
        `Redirecting to onboarding because no tenant was found`,
        {
          path: location.pathname,
          personalOrgsDisabled: personal_orgs_disabled,
          storedOrgId,
          lastTenantId: window.localStorage.getItem(LATEST_TENANT_STORAGE_KEY),
          instanceLoading: isPersonalOrgsLoading,
          tenantLoading: isLoading,
          isSSOLogin,
        }
      );
      navigate(`/onboarding`, { replace: true });
    } else {
      setRedirect(false);
    }
  }, [
    targetTenant,
    location.pathname,
    navigate,
    requiredInfoLoading,
    personal_orgs_disabled,
    storedOrgId,
    isLoading,
    isPersonalOrgsLoading,
    fallbackTenantError,
    infoError,
    isSSOLogin,
    datadogLogs,
  ]);

  // Special case for Self-hosted SSO and no tenant found.
  if (isSelfHostedSSO && targetTenant == null) {
    // If JIT provisioning is disabled, show the SSO empty state, as we don't expect the tenant to be provisioned by us. User will need to wait for SCIM or administrator to provision the tenant.
    if (!selfHostedJitProvisioningEnabled) {
      return <SSOEmptyState />;
    } else {
      // If JIT provisioning is enabled, show loading while polling briefly; then surface an error if we can't provision the tenant.
      return jitPollingExpired ? (
        <ErrorElement
          error={new Error('Unable to provision user to organization.')}
        />
      ) : (
        <LinearProgress />
      );
    }
  }

  if (requiredInfoLoading || redirect == null || redirect) {
    return <LinearProgress />;
  }

  if (fallbackTenantError || infoError) {
    return <ErrorElement error={fallbackTenantError || infoError} />;
  }

  // Show SSO empty state for SSO users with no tenants
  if (isSSOLogin && targetTenant == null && !requiredInfoLoading) {
    return <SSOEmptyState />;
  }

  return props.children;
}

// SSO users with access to the organization but no workspaces
function SSOEmptyState() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4 py-6">
      <div className="max-w-md text-center">
        <h1 className="mb-4 text-2xl font-semibold">No Access</h1>
        <p className="mb-6 text-gray-600">
          You have successfully authenticated via SSO, but you don't have access
          to any workspaces yet.
        </p>
        <p className="text-sm text-gray-500">
          Please contact your administrator to be added to a workspace.
        </p>
      </div>
    </div>
  );
}
